import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { Spinner } from './Spinner';
import { logger } from '../services/logger';
import { AiModel, AI_MODEL_PREFERENCE_KEY, API_TIMEOUT_PREF_KEY } from '../types';

interface ApiSettingsProps {
    title: string;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

const ConnectionSettings: React.FC = () => {
    const [apiUrl, setApiUrl] = useState('');
    const [timeout, setTimeoutValue] = useState('60');
    const [testStatus, setTestStatus] = useState<TestStatus>('idle');
    const [testMessage, setTestMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        const savedUrl = localStorage.getItem('apiUrl') || '';
        setApiUrl(savedUrl);
        apiService.getPreference<number>(API_TIMEOUT_PREF_KEY, 60).then(val => setTimeoutValue(String(val)));
    }, []);

    const handleTest = async () => {
        logger.info(`[UI] Kliknuto na tlačítko "Otestovat" s URL: "${apiUrl}"`);
        setTestStatus('testing');
        setTestMessage('');
        try {
            const result = await apiService.healthCheck(apiUrl);
            setTestStatus('success');
            setTestMessage(result.message);
        } catch (e: any) {
            setTestStatus('error');
            setTestMessage(e.message);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        if (apiUrl.trim()) {
            localStorage.setItem('apiUrl', apiUrl.trim());
        } else {
            localStorage.removeItem('apiUrl');
        }
        
        const timeoutNum = parseInt(timeout, 10);
        if (!isNaN(timeoutNum) && timeoutNum > 0) {
            await apiService.setPreference(API_TIMEOUT_PREF_KEY, timeoutNum);
        } else {
            await apiService.setPreference(API_TIMEOUT_PREF_KEY, 60); // Uložení výchozí hodnoty při neplatném vstupu
        }

        setIsSaving(false);
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            window.location.reload();
        }, 2000);
    };

    return (
        <>
            <p className="text-sm text-gray-600 mb-2">
                Zde můžete nastavit URL adresu backend serveru a časový limit pro API požadavky. Změny vyžadují znovunačtení aplikace.
            </p>
            <p className="text-sm text-gray-600 mb-6">
                Pro produkční nasazení nechte pole prázdné, aby se použila relativní cesta. Pro lokální vývoj zadejte celou adresu, např. <strong>http://localhost:8080</strong>.
            </p>
            
            <div className="space-y-4 max-w-xl">
                <div>
                    <label htmlFor="api-url-input" className="block text-sm font-medium text-gray-700 mb-1">
                        URL adresa backendu
                    </label>
                    <div className="flex items-center gap-2">
                         <input
                            type="url"
                            id="api-url-input"
                            value={apiUrl}
                            onChange={(e) => setApiUrl(e.target.value)}
                            placeholder="Prázdné pro produkci (relativní cesta)"
                            className="flex-grow p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                         <button
                            onClick={handleTest}
                            disabled={testStatus === 'testing'}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                        >
                            {testStatus === 'testing' ? <Spinner inline /> : 'Otestovat'}
                        </button>
                    </div>
                </div>
                <div>
                     <label htmlFor="api-timeout-input" className="block text-sm font-medium text-gray-700 mb-1">
                        Časový limit pro API (sekundy)
                    </label>
                     <input
                        type="number"
                        id="api-timeout-input"
                        value={timeout}
                        onChange={(e) => setTimeoutValue(e.target.value)}
                        placeholder="Výchozí: 60"
                        min="1"
                        className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-32"
                    />
                    <p className="text-xs text-gray-500 mt-1">Doporučeno pro pomalejší AI operace.</p>
                </div>
                {testStatus !== 'idle' && (
                    <div className={`p-3 rounded-md text-sm ${
                        testStatus === 'success' ? 'bg-green-100 text-green-800' : 
                        testStatus === 'error' ? 'bg-red-100 text-red-800' : ''
                    }`}>
                        {testMessage}
                    </div>
                )}
                <div className="flex items-center gap-4 pt-2">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
                    >
                        {isSaving ? 'Ukládám...' : 'Uložit a znovu načíst'}
                    </button>
                    {showSuccess && (
                        <span className="text-green-600 font-medium">
                            Uloženo! Aplikace se znovu načte...
                        </span>
                    )}
                </div>
            </div>
        </>
    );
};

const AiModelSelector: React.FC = () => {
    const [models, setModels] = useState<AiModel[]>([]);
    const [selectedModelName, setSelectedModelName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const availableModelsPromise = apiService.getAvailableAiModels();
                const savedPreferencePromise = apiService.getPreference<{ modelName: string }>(AI_MODEL_PREFERENCE_KEY, { modelName: '' });

                const [availableModels, savedPreference] = await Promise.all([availableModelsPromise, savedPreferencePromise]);
                
                setModels(availableModels);
                
                if (savedPreference && savedPreference.modelName) {
                    setSelectedModelName(savedPreference.modelName);
                } else {
                    const defaultModel = availableModels.find(m => m.isDefault);
                    if (defaultModel) {
                        setSelectedModelName(defaultModel.name);
                    } else if (availableModels.length > 0) {
                        setSelectedModelName(availableModels[0].name);
                    }
                }

            } catch (e: any) {
                setError('Nepodařilo se načíst modely: ' + e.message);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            await apiService.setPreference(AI_MODEL_PREFERENCE_KEY, { modelName: selectedModelName });
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } catch (e: any) {
            setError('Nepodařilo se uložit model: ' + e.message);
        } finally {
            setIsSaving(false);
        }
    };

    const selectedModelDetails = models.find(m => m.name === selectedModelName);

    return (
        <div className="bg-white p-6 rounded-lg shadow space-y-4">
            <h3 className="text-xl font-semibold text-gray-700">Výběr AI modelu</h3>
            <p className="text-sm text-gray-600">
                Vyberte model, který bude použit pro analýzu nahraných účtenek. Změny se projeví u všech nově nahrávaných souborů.
            </p>
            
            {isLoading && <Spinner text="Načítám dostupné modely..." />}
            
            {error && <div className="p-3 bg-red-100 text-red-700 text-sm rounded-md border border-red-200">{error}</div>}

            {!isLoading && !error && (
                 <div className="space-y-4 max-w-xl">
                    <div>
                        <label htmlFor="ai-model-select" className="block text-sm font-medium text-gray-700 mb-1">
                            Dostupné modely
                        </label>
                        <select
                            id="ai-model-select"
                            value={selectedModelName}
                            onChange={(e) => setSelectedModelName(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={models.length === 0}
                        >
                            {models.length === 0 && <option>Žádné modely nejsou k dispozici</option>}
                            {models.map(model => (
                                <option key={model.name} value={model.name}>
                                    {model.displayName}{model.isDefault ? ' (Výchozí)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedModelDetails && (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                            <p><strong>Popis:</strong> {selectedModelDetails.description}</p>
                        </div>
                    )}
                    
                    {models.length > 0 && models.length <= 1 && (
                         <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                           V budoucnu zde naleznete více modelů pro různé účely.
                        </div>
                    )}

                    <div className="flex items-center gap-4 pt-2">
                        <button
                            onClick={handleSave}
                            disabled={isSaving || !selectedModelName}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                        >
                            {isSaving ? 'Ukládám...' : 'Uložit model'}
                        </button>
                        {showSuccess && <span className="text-green-600 font-medium">Uloženo!</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ApiSettings: React.FC<ApiSettingsProps> = ({ title }) => {
    return (
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
                <ConnectionSettings />
            </div>
            <AiModelSelector />
        </div>
    );
};